import { twMerge } from 'tailwind-merge';
import { isCustomContent } from '@witmetrics/utils/keypoints';
import {
  useKeypoint,
  useKeypointType,
  useOrder,
  useOrderKeypointFromKeypointId,
  useProject,
  useProjectKeypointFromKeypointId,
} from '@witmetrics/data-store';
import type {
  CaseStatusContent,
  FileUploadedContent,
  OrderStatusContent,
} from '@witmetrics/utils';
import { formatUserName } from '@witmetrics/utils/strings';
import { KEYPOINT_TYPE_ID } from '@witmetrics/utils/constants';
import {
  checkMissingKeypointData,
  isStatusApplicable,
} from '@witmetrics/unison-core';
import OrderStatusChip from '@/components/OrderStatusChip';
import ProjectStatusChip from '@/components/ProjectStatusChip';
import { classes } from './utils';

const {
  CASE_CREATED,
  CASE_STATUS_CHANGE,
  ORDER_CREATED,
  ORDER_STATUS_CHANGE,
  FILES_UPLOADED,
  SEQUENCE_UPLOADED,
  ORDER_ADDED_TO_CASE,
} = KEYPOINT_TYPE_ID;

export type KeypointContentProps = {
  className?: string;
  keypointId: number;
};

export default function KeypointContent({
  className,
  keypointId,
}: KeypointContentProps) {
  const keypoint = useKeypoint(keypointId);
  const keypointType = useKeypointType(keypoint?.keypointTypeId);
  const projectKeypoint = useProjectKeypointFromKeypointId(keypointId);
  const project = useProject(projectKeypoint?.unisonProjectId);
  const orderKeypoint = useOrderKeypointFromKeypointId(keypointId);
  const order = useOrder(orderKeypoint?.orderId);

  const renderAuthor = (str: string) => {
    const createdBy = keypoint?.createdBy;
    return createdBy ? (
      <span>
        <span className={classes.bold}>{str}</span>
        {` by ${formatUserName(createdBy)}`}
      </span>
    ) : (
      <span className={classes.bold}>{str}</span>
    );
  };

  const missingData = checkMissingKeypointData(
    keypoint,
    order,
    orderKeypoint,
    project,
    projectKeypoint
  );

  if (!keypoint || missingData) {
    console.warn(`${missingData} (keypoint id ${keypointId}) not found`);
    return null;
  }

  const classList = twMerge(classes.content, className);

  if (isCustomContent(keypoint.content)) {
    return (
      <div className={classList}>
        {keypointType && (
          <div className={classes.bold}>{keypointType.label}</div>
        )}
        <div>{keypoint.content.message}</div>
      </div>
    );
  }
  switch (keypoint.keypointTypeId) {
    case CASE_CREATED:
      return <div className={classList}>{renderAuthor('Case created')}</div>;
    case CASE_STATUS_CHANGE:
      if (!isStatusApplicable(project?.unisonProjectTypeId)) return null;
      return (
        <div className={classList}>
          <span className={classes.bold}>Case status</span> changed to{' '}
          <ProjectStatusChip
            projectStatusId={
              (keypoint.content as CaseStatusContent).unisonProjectStatusId
            }
          />
        </div>
      );
    case ORDER_CREATED:
      return <div className={classList}>{renderAuthor('Order created')}</div>;
    case ORDER_ADDED_TO_CASE:
      return (
        <div className={classList}>{renderAuthor('Order added to case')}</div>
      );
    case ORDER_STATUS_CHANGE:
      return (
        <div className={classList}>
          <span className={classes.bold}>Order status</span> changed to{' '}
          <OrderStatusChip
            orderStatusId={
              (keypoint.content as OrderStatusContent).orderStatusId
            }
          />
        </div>
      );
    case FILES_UPLOADED:
      return (
        <div className={classList}>
          {renderAuthor(
            `File${(keypoint.content as FileUploadedContent).fileId ? '' : 's'} added`
          )}
        </div>
      );
    case SEQUENCE_UPLOADED:
      return <div className={classList}>{renderAuthor('Sequence added')}</div>;
    default:
      console.warn('Unknown keypoint type:', keypoint.keypointTypeId);
      return <div className={classList}>--</div>;
  }
}
